import { Button, Input } from "modules/common";
import { FC, useContext, useEffect, useState } from "react";
import { County } from "services/iot/models";
import { ReactComponent as DetailsIcon } from "assets/icons/details.svg";
import { ReactComponent as Spinner } from "assets/icons/spinner.svg";
import { Station } from "modules/common/components/Alarms/types/AlarmsForm";
import { Measurement, MeasurementType } from "modules/common/components/Alarms/constants";
import { IAlarmsFormProps } from "modules/common/components/Alarms/types/IAlarmsFormProps";
import { toast } from "react-toastify";
import { useAlarmFormQueries } from "modules/common/components/Alarms/Form/hooks/useAlarmFormQueries";
import CountiesStations from "modules/common/components/Alarms/Form/components/CountiesStations/CountiesStations";
import { getInitialFormData } from "modules/common/components/Alarms/Form/utils/GetInitialFormData";
import { onSubmitValidation } from "modules/common/components/Alarms/Form/utils/OnSubmitValidation";
import Measurements from "modules/common/components/Alarms/Form/components/Measurements/Measurements";
import { SelectedCountyStation, IThresholdUpdate, IValueRangeUpdate } from "../types/AlarmModels";
import { AccessibilityContext } from "views/AccessibilityContext";
import { classNames } from "helpers/ClassNameHelper";

const AlarmsForm: FC<IAlarmsFormProps> = props => {
    const { counties, stations, isLoading } = useAlarmFormQueries();

    const [groupName, setGroupName] = useState<string>("");
    const [selectedCounty, setSelectedCounty] = useState<County | null>(null);
    const [selectedStation, setSelectedStation] = useState<Station | null>(null);
    const [countiesStations, setCountiesStations] = useState<SelectedCountyStation[]>([]);
    const [selectedMeasurement, setSelectedMeasurement] = useState<Measurement | null>(null);
    const [measurements, setMeasurements] = useState<Measurement[]>([]);
    const { lowVision } = useContext(AccessibilityContext);

    useEffect(() => {
        setInitialState();
    }, [props.alarmGroupModalData, stations]);

    const setInitialState = () => {
        if (!props.alarmGroupModalData) {
            setGroupName("");
            setSelectedCounty(null);
            setSelectedStation(null);
            setSelectedMeasurement(null);
            setCountiesStations([]);
            setMeasurements([]);
            return;
        }

        setGroupName(props.alarmGroupModalData.name || "");

        const { newCountiesStations, newMeasurements } = getInitialFormData(
            props.alarmGroupModalData,
            stations
        );

        setCountiesStations(newCountiesStations);
        setMeasurements(newMeasurements);
    };

    const removeMeasurementType = (key: MeasurementType) => {
        setMeasurements(prev => prev.filter(m => m.id !== key));
    };

    const updateMeasurement = (id: MeasurementType, update: IThresholdUpdate) => {
        setMeasurements(prev =>
            prev.map(m => {
                if (m.id !== id) {
                    return m;
                }

                const result = { ...m };
                if ("amount" in update) {
                    result.amount = update.amount;
                }
                if ("lookbackDays" in update) {
                    result.lookbackDays = update.lookbackDays;
                }
                return result;
            })
        );
    };

    const onAmountChange = (key: MeasurementType, value: number | undefined) => {
        updateMeasurement(key, { amount: value });
    };

    const onLookbackDaysChange = (key: MeasurementType, value: number | undefined) => {
        updateMeasurement(key, { lookbackDays: value });
    };

    const updateValueRange = (id: MeasurementType, update: IValueRangeUpdate) => {
        setMeasurements(prev =>
            prev.map(m => {
                if (m.id !== id) {
                    return m;
                }

                const current = m.valueRange ?? { min: undefined, max: undefined };
                return {
                    ...m,
                    valueRange: {
                        min: "min" in update ? update.min : current.min,
                        max: "max" in update ? update.max : current.max,
                    },
                };
            })
        );
    };

    const onMinChange = (key: MeasurementType, value: number | undefined) => {
        updateValueRange(key, { min: value });
    };

    const onMaxChange = (key: MeasurementType, value: number | undefined) => {
        updateValueRange(key, { max: value });
    };

    const onCultureChange = (key: MeasurementType, value: number | undefined) => {
        setMeasurements(prev =>
            prev.map(m => {
                if (m.id !== key) {
                    return m;
                }
                return {
                    ...m,
                    cultureId: value,
                };
            })
        );
    };

    const onPhenophaseChange = (key: MeasurementType, value: number | undefined | null) => {
        setMeasurements(prev =>
            prev.map(m => {
                if (m.id !== key || value == null || value == undefined) {
                    return m;
                }
                return {
                    ...m,
                    phenophaseId: value,
                };
            })
        );
    };

    const addCountyStation = () => {
        const maxStations = 5;

        if (countiesStations.length >= maxStations) {
            toast.error(`Maksimalan broj stanica je ${maxStations}.`);
            return;
        }

        if (!selectedStation || !selectedCounty) {
            return;
        }

        const existing = countiesStations.find(
            s => s.stationId === selectedStation.id && s.countyId === selectedCounty?.id
        );

        if (existing) {
            toast.error("Ova stanica već postoji za odabranu županiju.");
            return;
        }

        setCountiesStations(prev => [
            ...prev,
            {
                countyId: selectedCounty.id,
                stationId: selectedStation.id,
                stationType: selectedStation.type,
            },
        ]);
        setSelectedStation(null);
    };

    const changeSelectedCounty = (
        currentCounty?: County,
        newCounty?: County,
        currentStation?: Station
    ) => {
        if (!currentCounty || !newCounty || !currentStation) return;

        const existing = countiesStations.find(
            s => s.countyId === currentCounty.id && s.stationId === currentStation.id
        );

        if (!existing) {
            toast.error("Nije pronađena stanica za odabranu županiju.");
            return;
        }

        const defaultStation = stations.find(s => s.zupanija?.id === newCounty.id);

        if (!defaultStation) {
            toast.error("Nije pronađena stanica za novu županiju.");
            return;
        }

        const updatedStations = countiesStations.map(s =>
            s.countyId === currentCounty.id && s.stationId === currentStation.id
                ? {
                      countyId: newCounty.id,
                      stationId: defaultStation.id,
                      stationType: defaultStation.type,
                  }
                : s
        );

        setCountiesStations(updatedStations);
    };

    const changeSelectedStation = (currentStation?: Station, newStation?: Station) => {
        if (!currentStation || !newStation) return;

        const prevExist = countiesStations.find(s => s.stationId === currentStation.id);

        if (!prevExist) {
            toast.error("Nije pronađena stanica za odabranu županiju.");
            return;
        }

        const newExist = countiesStations.find(s => s.stationId === newStation.id);

        if (newExist) {
            toast.error("Ova stanica već postoji za odabranu županiju.");
            return;
        }

        const updatedStations = countiesStations.map(s =>
            s.stationId === currentStation.id ? { ...s, stationId: newStation.id } : s
        );

        setCountiesStations(updatedStations);
    };

    const removeCountyStation = (county: County | undefined, station: Station | undefined) => {
        if (!county || !station) {
            return;
        }

        const updatedStations = countiesStations.filter(
            s => s.countyId !== county.id || s.stationId !== station.id
        );

        setCountiesStations(updatedStations);
    };

    const addMeasurement = () => {
        if (!selectedMeasurement) {
            return;
        }

        const existingMeasurement = measurements.find(m => m.id === selectedMeasurement.id);

        if (existingMeasurement) {
            toast.error("Ova vrsta mjerenja već postoji.");
            setSelectedMeasurement(null);
            return;
        }

        setMeasurements(prev => [...prev, selectedMeasurement]);
        setSelectedMeasurement(null);
    };

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            const result = onSubmitValidation(
                groupName,
                props.alarmGroups,
                props.alarmGroupModalData,
                countiesStations,
                measurements
            );

            if (!result) {
                return;
            }

            if (!props.alarmGroupModalData?.id) {
                props.createAlarmGroup(result);
                return;
            }

            props.updateAlarmGroup({
                ...result,
                groupId: props.alarmGroupModalData.id,
            });
        } catch (error) {
            console.error("Error creating/updating alarm definitions:", error);
        }
    };

    if (isLoading) {
        return (
            <div className="activation-container">
                <Spinner className="spinner spinner--large" />
            </div>
        );
    }

    return (
        <form onSubmit={onSubmit} className="alarms-form">
            <Input
                id="groupName"
                name="groupName"
                type="text"
                size="medium"
                value={groupName || ""}
                label="Naziv grupe alarma"
                placeholder="Naziv grupe alarma"
                onChange={e => setGroupName(e.target.value)}
            />

            <CountiesStations
                counties={counties}
                stations={stations}
                selectedCountyStations={countiesStations}
                selectedCounty={selectedCounty ?? undefined}
                selectedStation={selectedStation ?? undefined}
                setSelectedCounty={setSelectedCounty}
                setSelectedStation={setSelectedStation}
                addCountyStation={addCountyStation}
                removeCountyStation={removeCountyStation}
                changeSelectedCounty={changeSelectedCounty}
                changeSelectedStation={changeSelectedStation}
            />

            <p className="alarms-form-warning">
                <DetailsIcon className="alarms-form-details-icon" />
                Alarm će se aktivirati kada mjerenja prelaze donji i gornji prag ili količinu.
            </p>

            <Measurements
                selectedMeasurementType={selectedMeasurement ?? undefined}
                selectedMeasurementTypes={measurements}
                setSelectedMeasurementType={setSelectedMeasurement}
                onAmountChange={onAmountChange}
                onLookbackDaysChange={onLookbackDaysChange}
                onMaxChange={onMaxChange}
                onMinChange={onMinChange}
                onCultureChange={onCultureChange}
                onPhenophaseChange={onPhenophaseChange}
                removeMeasurementType={removeMeasurementType}
                addMeasurement={addMeasurement}
            />

            <div className={classNames("alarms-form-buttons", lowVision && "low-vision-darkgray")}>
                <Button
                    type="button"
                    size="small"
                    variant="primary-ghost"
                    onClick={props.closeAlarmGroupModal}
                    className={classNames(lowVision && "low-vision-black")}
                >
                    Zatvori
                </Button>
                <Button
                    type="submit"
                    size="small"
                    variant="primary"
                    className={classNames(lowVision && "low-vision-black")}
                >
                    {props.alarmGroupModalData?.id ? "Ažuriraj alarme" : "Kreiraj alarme"}
                </Button>
            </div>
        </form>
    );
};

export default AlarmsForm;
